import React from 'react';
import styles from './errorPage.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface ErrorBoundaryProps extends React.PropsWithChildren {
  resetKeys?: any[];
  onReset?: () => void;
  onError?: () => void;
  /** Custom error message to display instead of raw error */
  formatError?: (error: any) => React.ReactNode;
  /** Custom render callback for complete control over the fallback UI */
  fallbackRender?: (props: { error: any; resetErrorBoundary: () => void }) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: any;
}

/**
 * LocalErrorBoundary is a React error boundary component that catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * It supports custom error messages, custom fallback renderers, and automatic reset based on reset keys.
 */
export class LocalErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: any): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    this.props.onError?.();
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { error } = this.state;
    const { resetKeys } = this.props;

    if (error !== null) {
      const prevKeys = prevProps.resetKeys ?? [];
      const currentKeys = resetKeys ?? [];

      const hasChanged =
        prevKeys.length !== currentKeys.length || currentKeys.some((key, idx) => !Object.is(key, prevKeys[idx]));

      if (hasChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  resetErrorBoundary = () => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { formatError, fallbackRender, children } = this.props;

    if (error !== null) {
      // 1. Fully custom UI renderer if provided
      if (fallbackRender) {
        return fallbackRender({
          error,
          resetErrorBoundary: this.resetErrorBoundary,
        });
      }

      // Fallback to customErrorMessage or extract standard string
      let displayMessage: React.ReactNode;

      if (formatError) {
        displayMessage = formatError(error);
      } else if (error?.userError) {
        displayMessage = error.message;
      } else {
        displayMessage = 'An unexpected error happened';
      }

      return (
        <div className={styles.localError}>
          <pre>{displayMessage}</pre>
          <button type="button" className={styles.retryButton} onClick={this.resetErrorBoundary}>
            <FontAwesomeIcon icon="rotate-right" /> Try again
          </button>
        </div>
      );
    }

    return children;
  }
}
